import { ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/states";
import { formatDateTime } from "@/utils/dateUtils";
import { CRITERIOS_AVALIACAO_AGENCIA } from "@/types/avaliacao";
import { useAgenciaResumo } from "@/hooks/useAgenciaResumo";
import { useAgenciaAvaliacoesInternas } from "@/hooks/useAgenciaAvaliacoesInternas";

interface AgenciaAvaliacoesTabProps {
  agenciaId: string;
}

function CardMedia({ titulo, media, quantidade }: { titulo: string; media: number | null; quantidade: number }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{titulo}</CardTitle>
      </CardHeader>
      <CardContent>
        {media !== null ? (
          <p className="text-2xl font-semibold tabular-nums">
            {media}
            <span className="ml-1 text-sm font-normal text-muted-foreground">
              ({quantidade} {quantidade === 1 ? "avaliação" : "avaliações"})
            </span>
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">Sem avaliações ainda</p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Aba "Avaliações": médias bidirecionais (o que a agência avaliou dos
 * freelancers x o que os freelancers avaliaram da agência) e o detalhe das
 * avaliações internas por membro da equipe (avaliacoes_usuario_agencia).
 */
export function AgenciaAvaliacoesTab({ agenciaId }: AgenciaAvaliacoesTabProps) {
  const resumoQuery = useAgenciaResumo(agenciaId);
  // Consulta dedicada e leve (avaliacoes_usuario_agencia via PostgREST) — não
  // passa pela RPC get_agencia_timeline (payload de ~2,2MB), usada só pela
  // aba "Linha do tempo".
  const avaliacoesInternasQuery = useAgenciaAvaliacoesInternas(agenciaId);

  const carregando = resumoQuery.isLoading || avaliacoesInternasQuery.isLoading;
  const comErro = resumoQuery.isError || avaliacoesInternasQuery.isError;

  if (carregando) {
    return (
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-32" />
      </div>
    );
  }

  if (comErro) {
    return (
      <div className="space-y-2 py-6 text-center">
        <p className="text-sm text-muted-foreground">Não foi possível carregar as avaliações.</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            resumoQuery.refetch();
            avaliacoesInternasQuery.refetch();
          }}
        >
          Tentar de novo
        </Button>
      </div>
    );
  }

  const resumo = resumoQuery.data;
  const avaliacoesInternas = avaliacoesInternasQuery.data ?? [];

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <CardMedia
          titulo="Avaliações que a agência fez"
          media={resumo?.avaliacoes_dadas_media ?? null}
          quantidade={resumo?.avaliacoes_dadas_count ?? 0}
        />
        <CardMedia
          titulo="Avaliações que a agência recebeu dos freelancers"
          media={resumo?.avaliacoes_recebidas_media ?? null}
          quantidade={resumo?.avaliacoes_recebidas_count ?? 0}
        />
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium text-muted-foreground">Avaliações internas por membro</h3>
        {avaliacoesInternas.length === 0 ? (
          <EmptyState
            icon={ClipboardCheck}
            title="Nenhuma avaliação interna registrada"
            description="Avaliações feitas pela equipe da agência sobre freelancers aparecem aqui."
          />
        ) : (
          <ul className="space-y-2">
            {avaliacoesInternas.map((avaliacao, indice) => (
              <li key={indice} className="rounded-lg border p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">{avaliacao.membro}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(avaliacao.occurred_at)}
                  </span>
                </div>
                {avaliacao.notas && (
                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    {CRITERIOS_AVALIACAO_AGENCIA.filter((criterio) =>
                      Object.prototype.hasOwnProperty.call(avaliacao.notas ?? {}, criterio.slug),
                    ).map((criterio) => (
                      <span key={criterio.slug}>
                        {criterio.label}: {String((avaliacao.notas as Record<string, unknown>)[criterio.slug])}
                      </span>
                    ))}
                  </div>
                )}
                {avaliacao.comentario && <p className="mt-1.5 text-sm">{avaliacao.comentario}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default AgenciaAvaliacoesTab;
